import { useState, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { token } from '../theme/tokens.js';

export interface AppShellProps {
  /**
   * Product name, rendered as the header's `<h1>`. Optional — a consumer that
   * carries its brand mark elsewhere (the rail, say) may have no header copy
   * at all. The header itself is omitted whenever both this and
   * `headerActions` are absent, rather than rendering an empty bar.
   */
  title?: string;
  /**
   * Optional 64px icon rail at the far left. The design system's three-zone
   * shell is rail → sidebar → main; a single-domain product can omit the rail.
   */
  rail?: ReactNode;
  /** Primary navigation, rendered in the 236px sidebar column. */
  nav?: ReactNode;
  /** Header content aligned to the trailing edge — account menu, actions. */
  headerActions?: ReactNode;
  /**
   * Header content aligned to the leading edge, ahead of `title` — a back
   * control, typically. Separate from `title` because it is not the page's
   * `<h1>`, and separate from `headerActions` because that slot is pinned to
   * the trailing edge (see the `ml-auto` note below) — a consumer with no
   * `title` that put a back button there would have it dragged to the far
   * right alongside the account menu instead of sitting at the start.
   */
  headerLeading?: ReactNode;
  /**
   * Full-width strip above every landmark, for a standing fact about the
   * environment the app is running in — not for transient status.
   *
   * It sits outside the header rather than inside it because a warning that
   * lives among the controls reads as one more control. Nothing here is
   * dismissible: the condition it reports outlives the session.
   */
  banner?: ReactNode;
  /** Optional footer strip below the main region. */
  footer?: ReactNode;
  /**
   * Optional bottom navigation bar, shown only below `sm` (640px) — the mirror
   * of the `rail`, which is `hidden … sm:flex`. It is the rail's destinations in
   * the one place the rail cannot be: a phone, where the 64px column is gone.
   * A consumer with no rail, or one content to lose it on mobile, passes nothing.
   */
  bottomNav?: ReactNode;
  /** Accessible label for the sidebar landmark. */
  navLabel?: string;
  /** Accessible label for the rail landmark. */
  railLabel?: string;
  /** Accessible label for the bottom-nav landmark. Distinct from `railLabel`. */
  bottomNavLabel?: string;
  /** Accessible label for the rail's sidebar collapse/expand toggle. */
  navToggleLabel?: string;
  /**
   * Controlled sidebar-collapse state. Omit both this and `onNavOpenChange`
   * for the default uncontrolled behavior (an internal `useState(true)`,
   * `apps/admin`'s usage today) — passing one without the other still works,
   * but the button won't visibly move without `onNavOpenChange` wired up to
   * feed a new `navOpen` back in.
   */
  navOpen?: boolean;
  /** Fires with the flipped value on every collapse/expand click. */
  onNavOpenChange?: (open: boolean) => void;
  /**
   * Constrain the main region to the design system's reading column
   * (`--content-max`, 1000px). Tables and grids want more room than that —
   * but not *all* of it, so the main region carries its own outer cap either
   * way. See the `<main>` element below.
   */
  constrainContent?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Application frame — the design system's three-zone shell.
 *
 * `design/design-system/readme.md`: *"Three-zone shell: 64px icon rail + 236px
 * sidebar + flexible main. Reading content is centered in a 1000px column."*
 * The header is sticky and translucent with a backdrop blur, per the same
 * source.
 *
 * Landmarks are real (`banner` / `navigation` / `main` / `contentinfo`) so
 * assistive tech and `getByRole` queries both work without test ids.
 */
export function AppShell({
  title,
  rail,
  nav,
  headerActions,
  headerLeading,
  banner,
  footer,
  bottomNav,
  navLabel,
  railLabel,
  bottomNavLabel,
  navToggleLabel,
  navOpen: navOpenProp,
  onNavOpenChange,
  constrainContent = false,
  children,
  className,
}: AppShellProps) {
  const [navOpenState, setNavOpenState] = useState(true);
  // Controlled when the consumer passes `navOpen`, uncontrolled otherwise —
  // `apps/admin` passes neither today and keeps its existing session-only
  // behavior untouched.
  const navOpen = navOpenProp ?? navOpenState;

  function toggleNavOpen() {
    const next = !navOpen;
    onNavOpenChange?.(next);
    if (navOpenProp === undefined) setNavOpenState(next);
  }

  return (
    <div
      // `h-dvh`, not `min-h-screen`: a floor lets the shell grow past the
      // viewport when `<main>` is tall, which scrolls the whole document and
      // carries the rail and sidebar off-screen with it. A ceiling is what
      // makes the `flex-1`/`min-h-0`/`overflow-auto` chain on `<main>` below
      // actually clip, so the rail and sidebar stay pinned and only `<main>`
      // scrolls.
      //
      // `dvh`, not plain `vh`/`h-screen`: iOS Safari computes `100vh` against
      // its *largest* viewport (address bar and toolbar hidden), not the one
      // actually visible — so whenever that chrome is showing, the shell
      // claims more height than is on screen, and the rail/sidebar's last
      // rows fall under the fold with nothing able to scroll to them. `dvh`
      // (dynamic viewport height) tracks the real visible viewport live as
      // Safari's toolbar shows and hides.
      className={cn('flex h-dvh flex-col', className)}
      style={{ background: token('bp-app'), color: token('bp-text') }}
    >
      {banner ? (
        <div
          className="shrink-0 border-b px-6 py-2 text-sm font-medium"
          style={{
            // `bp-warning` rather than amber: the design system reserves amber
            // for time and for "active", and this is neither.
            borderColor: token('bp-warning'),
            background: `color-mix(in srgb, ${token('bp-warning')} 12%, transparent)`,
            color: token('bp-warning'),
          }}
        >
          {banner}
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        {rail ? (
          <nav
            aria-label={railLabel ?? 'Domains'}
            // `min-h-0 overflow-y-auto`: without its own scroll region, the
            // rail's content is clipped by the shell's height with no way to
            // reach whatever falls below the fold. `min-h-0` overrides a flex
            // child's default `min-height: auto`, which would otherwise let
            // it push past its row's bound instead of clipping and scrolling.
            className="hidden min-h-0 shrink-0 flex-col items-center gap-2 overflow-y-auto border-r py-3 sm:flex"
            style={{
              width: token('rail-width'),
              background: token('bp-rail'),
              borderColor: token('bp-border'),
            }}
          >
            {rail}

            {nav ? (
              <button
                type="button"
                onClick={toggleNavOpen}
                aria-expanded={navOpen}
                aria-label={navToggleLabel ?? 'Toggle sidebar'}
                className="mt-auto flex items-center justify-center rounded-md p-2 transition-colors hover:bg-elevated"
                style={{ color: token('bp-text-secondary') }}
              >
                <svg
                  viewBox="0 0 24 24"
                  width={16}
                  height={16}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  className="transition-transform"
                  style={{ transform: navOpen ? 'rotate(180deg)' : undefined }}
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            ) : null}
          </nav>
        ) : null}

        {nav && navOpen ? (
          <nav
            aria-label={navLabel ?? title}
            // See the rail's own `<nav>` above for why this carries its own
            // scroll region rather than relying solely on the shell's height.
            className="hidden min-h-0 shrink-0 overflow-y-auto border-r p-3 md:block"
            style={{
              width: token('sidebar-width'),
              background: token('bp-sidebar'),
              borderColor: token('bp-border'),
            }}
          >
            {nav}
          </nav>
        ) : null}

        <div className="flex min-w-0 flex-1 flex-col">
          {title || headerActions || headerLeading ? (
            <header
              // `relative`: `apps/web`'s mobile header centers its brand mark
              // by taking it out of flow and positioning it against this
              // element (`App.tsx`'s `HeaderBrand`) rather than against
              // whichever sibling it happens to render beside — a back
              // button on some routes, nothing on others. No visible effect
              // here on its own; it only matters to a descendant that opts
              // into `absolute`.
              className="relative sticky top-0 z-20 flex h-14 shrink-0 items-center justify-between gap-4 px-6 backdrop-blur-md"
              style={{
                // Translucent so content scrolls under it — the readme's sticky
                // header treatment (`bg-[#0a1120]/80` + `backdrop-blur-md`).
                background: `color-mix(in srgb, ${token('bp-app')} 80%, transparent)`,
              }}
            >
              {headerLeading ? (
                <div className="flex items-center gap-2">{headerLeading}</div>
              ) : null}
              {title ? (
                <h1 className="truncate text-base font-semibold tracking-tight">{title}</h1>
              ) : null}
              {/*
                `ml-auto`, not `justify-between` alone. `title` and
                `headerLeading` are both optional, and a consumer that owns its
                `<h1>` on the page and has no back control passes neither —
                which leaves this the header's only child, and a lone child
                under `justify-between` sits at the *start*. That put the
                account menu against the sidebar instead of the trailing edge.
                With a leading sibling present the margin changes nothing.
              */}
              {headerActions ? (
                <div className="ml-auto flex items-center gap-2">{headerActions}</div>
              ) : null}
            </header>
          ) : null}

          {/*
            The cap is on `<main>` itself rather than on a wrapper inside it, so
            a surface that opts out of the reading column still gets a row it can
            scan across. `constrainContent` narrows further, to the design
            system's 1000px reading column, for prose.
          */}
          <main className="mx-auto min-h-0 w-full max-w-7xl flex-1 overflow-auto px-6 py-6">
            {constrainContent ? (
              <div className="mx-auto w-full" style={{ maxWidth: token('content-max') }}>
                {children}
              </div>
            ) : (
              children
            )}
          </main>

          {footer ? (
            <footer
              className="shrink-0 border-t px-6 py-3 text-sm"
              style={{
                borderColor: token('bp-border'),
                background: token('bp-sidebar'),
                color: token('bp-text-secondary'),
              }}
            >
              {footer}
            </footer>
          ) : null}

          {bottomNav ? (
            // `sm:hidden` — the exact mirror of the rail's `sm:flex`. Below
            // 640px the rail is gone and this is on screen; at 640px and up it
            // is `display:none` (and so out of the a11y tree) and the rail is
            // back. Rail and sidebar are already hidden at this width, so the
            // content column spans the screen and this bar spans it too.
            <nav
              aria-label={bottomNavLabel ?? 'Sections'}
              className="shrink-0 border-t sm:hidden"
              style={{ background: token('bp-rail'), borderColor: token('bp-border') }}
            >
              {bottomNav}
            </nav>
          ) : null}
        </div>
      </div>
    </div>
  );
}
